import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Lock, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const { login, signup } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const redirect = searchParams.get('redirect') || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(email || username, password);
        navigate(redirect);
      } else {
        if (password !== confirmPassword) {
          setError(t('login.passwordMismatch'));
          setLoading(false);
          return;
        }
        await signup(email, username, password);
        navigate(redirect);
      }
    } catch (err) {
      setError(err.message || t('login.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="-mt-16">
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-[#4796E3]/12 via-[#4796E3]/08 to-transparent rounded-full blur-[100px]" />
          <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] bg-gradient-to-tr from-emerald-600/8 to-transparent rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 w-full max-w-md mx-auto px-4 py-24">
          <div className="border-gradient bg-dark-card rounded-2xl shadow-dark-card p-8">
            <h1 className="text-3xl font-bold text-white mb-2 text-center">
              {isLogin ? t('login.title') : t('login.registerTitle')}
            </h1>
            <p className="text-center text-gray-500 mb-8">
              {isLogin ? t('login.welcome') : t('login.joinTitle')}
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('login.email')}</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-500 group-focus-within:text-accent transition-colors" />
                  </div>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
                    className="block w-full pl-10 pr-4 py-2.5 bg-dark-surface border border-dark-border rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none transition-all duration-300" required />
                </div>
              </div>
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('login.username')}</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-500 group-focus-within:text-accent transition-colors" />
                    </div>
                    <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t('login.username')}
                      className="block w-full pl-10 pr-4 py-2.5 bg-dark-surface border border-dark-border rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none transition-all duration-300" required={!isLogin} />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('login.password')}</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-500 group-focus-within:text-accent transition-colors" />
                  </div>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('login.passwordPlaceholder')}
                    className="block w-full pl-10 pr-4 py-2.5 bg-dark-surface border border-dark-border rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none transition-all duration-300" required />
                </div>
              </div>
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('login.confirmPassword')}</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-500 group-focus-within:text-accent transition-colors" />
                    </div>
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t('login.confirmPasswordPlaceholder')}
                      className="block w-full pl-10 pr-4 py-2.5 bg-dark-surface border border-dark-border rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none transition-all duration-300" required={!isLogin} />
                  </div>
                </div>
              )}
              <button type="submit" disabled={loading}
                className="w-full py-3.5 bg-white text-dark rounded-full font-semibold hover:shadow-glow-white transition-all duration-400 disabled:opacity-40 disabled:cursor-not-allowed mt-2">
                {loading ? t('login.processing') : isLogin ? t('login.submit') : t('login.register')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button onClick={() => { setIsLogin(!isLogin); setError(''); }}
                className="text-gray-400 hover:text-accent text-sm transition-colors">
                {isLogin ? t('login.noAccount') : t('login.hasAccount')}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
